import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@clerk/clerk-react';
import { useUserRole } from '../hooks/useUserRole';
import { EOLBadge } from './EOLBadge';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

interface OSVersion {
  id: number;
  product_id: number;
  version_number: string;
  display_version: string;
  build_number: string;
  kernel_version: string;
  architecture: string;
  feature_update: string;
  update_classification: string;
  is_security_update: boolean;
  vendor_advisory_id: string;
  vendor_advisory_url: string;
  release_date: string;
  release_channel: string;
  is_latest: boolean;
  product_name: string;
  manufacturer: string;
  family_name: string;
  cpe_vendor: string;
  cpe_product: string;
  cpe_base: string;
  // AI Enrichment
  deployment_priority_score?: number;
  importance_level?: string;
  patch_details?: string;
  deployment_complexity?: string;
  recommended_action?: string;
  business_impact_analysis?: string;
  deployment_notes?: string;
  new_features?: string[];
  bug_fixes?: string[];
  breaking_changes?: string[];
  release_summary?: string;
  // EOL tracking
  eol_status?: string;
  days_until_eol?: number;
  eol_risk_level?: string;
  eol_cycle?: string;
  eol_date?: string;
  eol_support_date?: string;
  eol_is_lts?: boolean;
}

interface CVE {
  cve_number: string;
  description: string;
  severity: string;
  cvss_score: number;
  published_date: string;
  status: string;
  nvd_url: string;
}

interface OSVersionDetailsSlideoutProps {
  versionId: number | null;
  onClose: () => void;
}

export function OSVersionDetailsSlideout({ versionId, onClose }: OSVersionDetailsSlideoutProps) {
  const [version, setVersion] = useState<OSVersion | null>(null);
  const [cves, setCves] = useState<CVE[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { getToken } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();

  useEffect(() => {
    if (versionId) {
      loadVersionDetails();
    }
  }, [versionId]);

  const loadVersionDetails = async () => {
    if (!versionId) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/os/versions/${versionId}`);
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setVersion(data);
      setCves(data.cves || []);
    } catch (err) {
      console.error('Failed to load version details:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!versionId) return null;

  const getSeverityColor = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'fixed':
        return 'bg-green-100 text-green-800';
      case 'affected':
        return 'bg-red-100 text-red-800';
      case 'unaffected':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const buildCPE = () => {
    if (!version) return '';

    // Construct feature-specific CPE product if feature_update exists
    let cpeProduct = version.cpe_product;
    if (version.feature_update && (version.cpe_product === 'windows_11' || version.cpe_product === 'windows_10')) {
      cpeProduct = `${version.cpe_product}_${version.feature_update.toLowerCase()}`;
    }

    // Build full CPE URI
    return `cpe:2.3:o:${version.cpe_vendor}:${cpeProduct}:${version.version_number}:*:*:*:*:*:${version.architecture}:*`;
  };

  const handleSyncVersion = async () => {
    if (!versionId || syncing) return;

    try {
      setSyncing(true);
      setSyncMessage(null);

      const token = await getToken();
      const response = await fetch(`${API_BASE_URL}/os/versions/${versionId}/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to enqueue CVE sync');
      }

      await response.json();
      setSyncMessage({
        type: 'success',
        text: 'Version successfully enqueued for CVE sync. Processing will begin shortly.',
      });

      // Auto-dismiss success message after 5 seconds
      setTimeout(() => setSyncMessage(null), 5000);
    } catch (error) {
      console.error('Failed to sync version:', error);
      setSyncMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to enqueue CVE sync',
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

      {/* Slideout */}
      <div className="absolute inset-y-0 right-0 max-w-2xl w-full bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">OS Version Details</h2>
          <div className="flex items-center gap-2">
            {!roleLoading && isAdmin && (
              <button
                onClick={handleSyncVersion}
                disabled={syncing}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                title="Manually sync CVEs for this version"
              >
                <ArrowPathIcon className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync CVEs'}
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-6 h-6 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Sync Message */}
        {syncMessage && (
          <div className={`mx-6 mt-4 p-4 rounded-lg ${
            syncMessage.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            <div className="flex items-start gap-2">
              <span className="text-sm font-medium">{syncMessage.text}</span>
              <button
                onClick={() => setSyncMessage(null)}
                className="ml-auto text-sm underline hover:no-underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : version ? (
            <div className="space-y-6">
              {/* Version Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Version Information</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Product</span>
                    <span className="text-sm font-medium text-gray-900">{version.product_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Version</span>
                    <span className="text-sm font-medium text-gray-900">
                      {version.display_version || version.version_number}
                    </span>
                  </div>
                  {version.build_number && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Build Number</span>
                      <span className="text-sm font-mono text-gray-900">{version.build_number}</span>
                    </div>
                  )}
                  {version.kernel_version && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Kernel Version</span>
                      <span className="text-sm font-mono text-gray-900">{version.kernel_version}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Architecture</span>
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {version.architecture}
                    </span>
                  </div>
                  {version.feature_update && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Feature Update</span>
                      <span className="text-sm font-medium text-gray-900">{version.feature_update}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">CPE</span>
                    <span className="text-xs font-mono text-gray-900 break-all text-right max-w-md">
                      {buildCPE()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Classification</span>
                    <span className="text-sm font-medium text-gray-900 capitalize">
                      {version.update_classification || 'N/A'}
                    </span>
                  </div>
                  {version.vendor_advisory_id && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Advisory</span>
                      {version.vendor_advisory_url ? (
                        <a
                          href={version.vendor_advisory_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {version.vendor_advisory_id} →
                        </a>
                      ) : (
                        <span className="text-sm font-medium text-gray-900">
                          {version.vendor_advisory_id}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Release Date</span>
                    <span className="text-sm text-gray-900">
                      {version.release_date
                        ? new Date(version.release_date).toLocaleDateString()
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Status</span>
                    <div className="flex gap-2">
                      {version.is_latest && (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Latest
                        </span>
                      )}
                      {version.is_security_update && (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          Security Update
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* EOL Status Section */}
              {version.eol_date && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">End of Life Status</h3>
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4">
                    <div className="mb-4">
                      <EOLBadge
                        eolDate={version.eol_date}
                        supportDate={version.eol_support_date || null}
                        isActive={version.eol_status === 'supported'}
                        isLTS={version.eol_is_lts}
                      />
                    </div>

                    <div className="space-y-3">
                      {version.eol_cycle && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">EOL Cycle</span>
                          <span className="text-sm font-medium text-gray-900">{version.eol_cycle}</span>
                        </div>
                      )}

                      {version.eol_date && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">EOL Date</span>
                          <span className="text-sm font-medium text-gray-900">
                            {new Date(version.eol_date).toLocaleDateString()}
                          </span>
                        </div>
                      )}

                      {version.eol_support_date && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Support End Date</span>
                          <span className="text-sm font-medium text-gray-900">
                            {new Date(version.eol_support_date).toLocaleDateString()}
                          </span>
                        </div>
                      )}

                      {version.days_until_eol !== undefined && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Days Until EOL</span>
                          <span className={`text-sm font-medium ${
                            version.days_until_eol < 0
                              ? 'text-red-600'
                              : version.days_until_eol < 90
                              ? 'text-yellow-600'
                              : 'text-green-600'
                          }`}>
                            {version.days_until_eol < 0
                              ? `EOL ${Math.abs(version.days_until_eol)} days ago`
                              : `${version.days_until_eol} days remaining`}
                          </span>
                        </div>
                      )}

                      {version.eol_risk_level && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Risk Level</span>
                          <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                            version.eol_risk_level === 'eol' ? 'bg-red-100 text-red-800' :
                            version.eol_risk_level === 'critical' ? 'bg-orange-100 text-orange-800' :
                            version.eol_risk_level === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {version.eol_risk_level.toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 text-xs text-gray-500 text-center">
                      Data provided by{' '}
                      <a
                        href="https://endoflife.date"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:text-purple-800 underline"
                      >
                        endoflife.date
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* AI Enrichment Section */}
              {version.deployment_priority_score && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Analysis</h3>
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 space-y-4">

                    {/* Priority Score & Recommended Action */}
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-sm text-gray-600 mb-1">Deployment Priority</div>
                        <div className="flex items-center gap-3">
                          <div className="text-3xl font-bold text-indigo-600">
                            {version.deployment_priority_score.toFixed(1)}
                          </div>
                          <div className="text-sm text-gray-500">/10</div>
                          {version.importance_level && (
                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                              version.importance_level === 'critical' ? 'bg-red-100 text-red-800' :
                              version.importance_level === 'high' ? 'bg-orange-100 text-orange-800' :
                              version.importance_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {version.importance_level.toUpperCase()}
                            </span>
                          )}
                        </div>
                      </div>
                      {version.deployment_complexity && (
                        <div className="text-right">
                          <div className="text-sm text-gray-600 mb-1">Complexity</div>
                          <span className={`px-3 py-1 text-sm font-semibold rounded-full ${
                            version.deployment_complexity === 'complex' ? 'bg-red-100 text-red-800' :
                            version.deployment_complexity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {version.deployment_complexity}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Recommended Action */}
                    {version.recommended_action && (
                      <div className="bg-white rounded-lg p-3 border-l-4 border-indigo-500">
                        <div className="text-sm font-semibold text-gray-700 mb-1">Recommended Action</div>
                        <div className="text-sm text-gray-900">{version.recommended_action}</div>
                      </div>
                    )}

                    {/* Release Summary */}
                    {version.release_summary && (
                      <div>
                        <div className="text-sm font-semibold text-gray-700 mb-2">Summary</div>
                        <p className="text-sm text-gray-900 leading-relaxed">{version.release_summary}</p>
                      </div>
                    )}

                    {/* Patch Details */}
                    {version.patch_details && (
                      <div>
                        <div className="text-sm font-semibold text-gray-700 mb-2">What This Patch Fixes</div>
                        <p className="text-sm text-gray-900 leading-relaxed">{version.patch_details}</p>
                      </div>
                    )}

                    {/* Breaking Changes */}
                    {version.breaking_changes && version.breaking_changes.length > 0 && (
                      <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                        <div className="text-sm font-semibold text-red-900 mb-2">⚠️ Breaking Changes</div>
                        <ul className="list-disc list-inside space-y-1">
                          {version.breaking_changes.map((change, idx) => (
                            <li key={idx} className="text-sm text-red-800">{change}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Bug Fixes */}
                    {version.bug_fixes && version.bug_fixes.length > 0 && (
                      <div>
                        <div className="text-sm font-semibold text-gray-700 mb-2">Bug Fixes</div>
                        <ul className="list-disc list-inside space-y-1">
                          {version.bug_fixes.map((fix, idx) => (
                            <li key={idx} className="text-sm text-gray-700">{fix}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* New Features */}
                    {version.new_features && version.new_features.length > 0 && (
                      <div>
                        <div className="text-sm font-semibold text-gray-700 mb-2">New Features</div>
                        <ul className="list-disc list-inside space-y-1">
                          {version.new_features.map((feature, idx) => (
                            <li key={idx} className="text-sm text-gray-700">{feature}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Business Impact */}
                    {version.business_impact_analysis && (
                      <div>
                        <div className="text-sm font-semibold text-gray-700 mb-2">Business Impact</div>
                        <p className="text-sm text-gray-900">{version.business_impact_analysis}</p>
                      </div>
                    )}

                    {/* Deployment Notes */}
                    {version.deployment_notes && (
                      <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                        <div className="text-sm font-semibold text-yellow-900 mb-1">Deployment Notes</div>
                        <p className="text-sm text-yellow-800">{version.deployment_notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* CVEs */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Associated CVEs ({cves.length})
                </h3>
                {cves.length > 0 ? (
                  <div className="space-y-4">
                    {cves.map((cve, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/vulnerabilities/${cve.cve_number}`}
                              className="font-mono text-sm font-semibold text-blue-600 hover:text-blue-800"
                            >
                              {cve.cve_number}
                            </Link>
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(cve.status)}`}>
                              {cve.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor(cve.severity)}`}>
                              {cve.severity?.toUpperCase()}
                            </span>
                            {cve.cvss_score && (
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                CVSS {cve.cvss_score}
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{cve.description}</p>
                        <div className="text-xs text-gray-500">
                          Published: {cve.published_date ? new Date(cve.published_date).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <p className="text-gray-600">No CVEs associated with this version</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600">Failed to load version details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
